import { OrderProcessBottomSheet } from '@components/BottomSheet/OrderProcessBottomSheet';
import { ButcherHeader } from '@components/ButcherHeader';
import { OrderNotesModal } from '@components/Modal/OrderProcessing/OrderNotesModal';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { OrderStatusBadge } from '@components/OrderStatusBadge';
import { useToast } from '@contexts/Toast';
import { faCircleCheck, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Axios } from '@helpers/ApiService';
import {
  errorMessage,
  Length,
  OrderProcessingMasterData,
  OrderProcessStatus,
  ProductProcessingCrumbSteps,
  ProductType,
  responseTexts,
  timers,
  WeightType,
} from '@helpers/Constants';
import { FrontendRoutes, ApiEndpoints } from '@helpers/Constants/Navigation';
import { ProductProcessingOrderSpecies } from '@helpers/Models';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const ProductProcessingTaskDetails: React.FC = () => {
  const location = useLocation();
  const { speciesId, speciesName, orderNumber } = location.state || {};
  const [productDetails, setProductDetails] =
    useState<ProductProcessingOrderSpecies>({
      orderId: 0,
      orderSpeciesId: 0,
      orderNumber: '',
      speciesTag: '',
      imageUrl: '',
      orderCheckinInches: 0,
      orderCheckinFeet: 0,
      notes: {
        orderCheckinNotes: '',
        orderSpeciesNotes: '',
        orderServiceNotes: [],
      },
      products: [],
    });
  const { showToastMessage } = useToast();
  const navigate = useNavigate();
  const [searchKey, setSearchKey] = useState('');
  const [showNotesModal, setShowNotesModal] = useState<boolean>(false);
  const [currentProductName, setCurrentProductName] = useState<string>('');
  const [currentProductId, setCurrentProductId] = useState<number>();

  // For displaying order process step options - "Start Processing" and "Complete Processing"
  const [showOrderProcessModal, setShowOrderProcessModal] =
    useState<boolean>(false);

  /**
   * useEffect hook to handle order detail retrieval on component mount or when `orderId` changes.
   *
   * - If `orderId` is available, it triggers `fetchproductDetails()` to load the corresponding order.
   * - If `orderId` is not present, it shows an error toast message and navigates back to the
   *   Product Processing List page after a short delay.
   *
   * This hook re-runs whenever the `orderId` value changes.
   */
  useEffect(() => {
    console.log(productDetails);
    if (speciesId) {
      fetchproductDetails();
    } else {
      showToastMessage(errorMessage.INVALID_REQUEST, 'error');
      setTimeout(() => {
        navigate(FrontendRoutes.PRODUCT_PROCESSING_LIST);
      }, timers.TOAST_AUTO_CLOSE); //4000ms
    }
  }, [speciesId]);

  /**
   * Fetches detailed information about a specific order using its ID.
   *
   * Makes a POST request to the `ORDER_DETAILS` API endpoint with the provided `orderId`,
   * retrieves the order data, and updates local state variables:
   * - `orderNumber` (order identifier),
   * - `productDetails` (full order data),
   * - `estCompletionDate` (parsed estimated completion date).
   *
   * Displays a toast message if the request fails.
   *
   * @async
   * @function fetchproductDetails
   * @returns {Promise<void>} A Promise that resolves when the data is successfully fetched and state is updated.
   * @throws Will throw an error if the API call fails.
   */
  const fetchproductDetails = async (): Promise<void> => {
    try {
      const response = await Axios.post(
        ApiEndpoints.PRODUCT_PROCESSING_TASK_DETAILS,
        {
          orderSpeciesId: speciesId,
        },
      );
      setProductDetails(response.data.data[0]);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
      throw error;
    }
  };

  /**
   * Handles the workflow for processing a product, including starting, completing,
   * and navigating to product details.
   *
   * @async
   * @param {number} statusNumber - The new status for the product (e.g., in-progress, completed).
   * @param {number} [productId] - (Optional) The unique ID of the product. Used when navigating to product details.
   * @param {string} [productName] - (Optional) The name of the product. Used when navigating to product details.
   *
   * @returns {Promise<void>} Resolves when the workflow update and navigation are complete.
   *
   * Behavior:
   * - If the status is `completed` or higher, navigates directly to the product's in-progress details page.
   * - Otherwise, closes the process modal, updates the product's status via API,
   *   and refreshes product details.
   *
   * API Call:
   * - `POST` request to `PRODUCT_PROCESS_WORKFLOW` with:
   *    - `productId`: The current product ID.
   *    - `status`: Sets to `inprogress` if `statusNumber` is `1`, else `completed`.
   *
   * Side Effects:
   * - Shows toast messages for success or error.
   * - Calls `fetchproductDetails()` to refresh product data.
   *
   */
  const handleProductProcessing = async (
    statusNumber: number,
    productId?: number,
    productName?: string,
  ): Promise<void> => {
    if (statusNumber >= OrderProcessStatus.COMPLETED) {
      // Navigate to product details
      navigate(FrontendRoutes.PRODUCT_INPROGRESS_DETAILS, {
        state: {
          speciesId,
          productName,
          orderNumber,
          editMode: false,
          productId,
          speciesName,
        },
      });
      return;
    }
    setShowOrderProcessModal(false);

    try {
      await Axios.post(ApiEndpoints.PRODUCT_PROCESS_WORKFLOW, {
        productId: currentProductId,
        status:
          statusNumber == 1
            ? OrderProcessStatus.INPROGRESS
            : OrderProcessStatus.COMPLETED, // add if status = 1 or 0 condition
      });
      // if 2 - add final measurements
      showToastMessage(responseTexts.PRODUCT_WORKFLOW_START_SUCCESS, 'success');
      fetchproductDetails();
    } catch (error) {
      showToastMessage(errorMessage.ERROR_UPDATING_PRODUCT_WORKFLOW, 'error');
    }
  };

  /**
   * Prepares the UI for updating a product's processing status.
   *
   * @param {number} id - The unique identifier of the product whose status is being updated.
   * @param {string} name - The name of the product.
   *
   * This function:
   * 1. Opens the order process modal.
   * 2. Sets the currently selected product's name and ID in state.
   *
   * @example
   * handleUpdateStatus(101, 'Deluxe Leather Bag');
   */
  const handleUpdateStatus = (id: number, name: string): void => {
    setShowOrderProcessModal(true);
    setCurrentProductName(name);
    setCurrentProductId(id);
  };

  /**
   * Navigates to the completed product details page.
   *
   * @param {number} productId - The unique identifier of the product to view.
   * @param {string} productName - The name of the product to display in the details view.
   *
   * This function uses React Router's `navigate` to redirect the user to the
   * PRODUCT_COMPLETED_DETAILS route, passing necessary product and order details
   * via the route state.
   *
   * @example
   * viewCompletedProduct(42, 'Premium Cotton Fabric');
   */
  const viewCompletedProduct = (
    productId: number,
    productName: string,
  ): void => {
    navigate(FrontendRoutes.PRODUCT_COMPLETED_DETAILS, {
      state: { speciesId, productName, orderNumber, productId, speciesName },
    });
  };

  /**
   * Props configuration for the breadcrumb navigation in the Product Processing page.
   *
   * @property {Array} steps - List of breadcrumb steps to display in order.
   * @property {string} activeItem - The label for the currently active breadcrumb item.
   * @property {string | number} orderNumber - The order number associated with the processing flow.
   * @property {string} type - The type of order processing, set to PRODUCT_PROCESSING.
   *
   * Used to provide navigation context in the UI so users know where they are in the workflow.
   *
   * @example
   * const BreadCrumbsProps = {
   *   steps: ProductProcessingCrumbSteps,
   *   activeItem: 'Product Processing -',
   *   orderNumber: orderNumber,
   *   type: OrderProcessingMasterData.PRODUCT_PROCESSING,
   * };
   */
  const BreadCrumbsProps = {
    steps: ProductProcessingCrumbSteps,
    activeItem: 'species_name',
    orderNumber: orderNumber,
    speciesName: speciesName,
    type: OrderProcessingMasterData.PRODUCT_PROCESSING,
    stateProp: { orderId: productDetails.orderId },
  };

  return (
    <div className="flex h-screen w-screen gap-1">
      {/* Sidebar */}
      <SideNav active={5} />
      {/* Main Content */}
      <div className="flex-1 p-1 overflow-auto relative">
        {/* Header */}
        <ButcherHeader />
        <OrderListBreadcrumbs orderInfo={BreadCrumbsProps} />
        <div className="p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-gray-700">
              <button
                type="button"
                onClick={() => window.history.back()}
                className="p-1"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071L4.29289 7.70711C3.90237 7.31658 3.90237 6.68342 4.29289 6.29289L9.29289 1.29289C9.68342 0.902369 10.3166 0.902369 10.7071 1.29289C11.0976 1.68342 11.0976 2.31658 10.7071 2.70711L6.41421 7L10.7071 11.2929C11.0976 11.6834 11.0976 12.3166 10.7071 12.7071Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              <h2 className="text-lg font-semibold text-gray-900">
                {speciesName}
                {productDetails.orderCheckinInches &&
                  productDetails.orderCheckinFeet && (
                    <>
                      {''} ({productDetails.orderCheckinFeet} {Length.FEET}{' '}
                      {productDetails.orderCheckinInches} {Length.INCHES})
                    </>
                  )}
              </h2>
            </div>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setShowNotesModal(true)}
                className="text-primary font-bold ml-2"
              >
                View Notes
              </button>
            </div>
            {/* Order Notes */}
            {showNotesModal && (
              <OrderNotesModal
                speciesName={productDetails?.speciesTag ?? ''}
                onClose={() => setShowNotesModal(false)}
                checkInNotes={
                  productDetails?.notes.orderCheckinNotes != ''
                    ? (productDetails?.notes.orderCheckinNotes ?? '')
                    : errorMessage.NO_NOTES_FOUND
                }
                AdditionalNotes={
                  productDetails?.notes.orderSpeciesNotes != ''
                    ? (productDetails?.notes.orderSpeciesNotes ?? '')
                    : errorMessage.NO_NOTES_FOUND
                }
                ServiceNotes={productDetails.notes.orderServiceNotes}
              />
            )}
          </div>
          <div className="flex justify-between items-center">
            <h1 className="font-semibold text-xl mt-3">Products</h1>
            <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search..."
                  onChange={(e) => setSearchKey(e.target.value)}
                  value={searchKey}
                  className="border p-2 pl-3 pr-10 w-80 accent-primary focus:outline-primary border-gray-300 rounded"
                />
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                />
              </div>
            </div>
          </div>
          {productDetails.products
            ?.filter((product) =>
              searchKey.trim() === ''
                ? true
                : product.productName
                    .toString()
                    .toLowerCase()
                    .includes(searchKey.trim().toLowerCase()),
            )
            .map((product) => (
              <div
                key={product.orderProductId}
                onClick={() => {
                  if (product.productStatus >= OrderProcessStatus.COMPLETED) {
                    viewCompletedProduct(
                      product.orderProductId,
                      product.productName,
                    );
                  }
                }}
                className={`mt-3 p-3 rounded-lg shadow-md bg-[#FFFEFA] ${product.productStatus >= OrderProcessStatus.COMPLETED ? 'cursor-pointer' : ''}`}
              >
                <div>
                  <div className="flex justify-between items-center">
                    <h1 className="font-semibold text-xl mt-3">
                      {product.productName}
                      {product.fatPercent > 0 && `, ${product.fatPercent}% Fat`}
                    </h1>
                    <OrderStatusBadge
                      status={product.productStatus}
                      type="productStatus"
                    />
                  </div>

                  <div className="bg-[#F1F1F1] rounded-md shadow-md mt-5 p-3 border-[#00000033]">
                    <span className="font-semibold">Order Details</span>
                    <div className="flex items-center justify-between py-2 rounded-md">
                      <div>
                        <span className="text-[#43474E]">
                          {product.productType == ProductType.WEIGHT
                            ? 'Total Weight: '
                            : 'Total Quantity: '}
                        </span>
                        <span className="text-gray-800 font-semibold">
                          {/* If product is processed, display the final measurements */}
                          {product.productStatus >= OrderProcessStatus.COMPLETED
                            ? product.productFinalQuantity
                            : product.productQuantity}{' '}
                          {product.productType == ProductType.WEIGHT
                            ? `${WeightType.LBS}`
                            : ''}
                        </span>
                      </div>
                      {product.productType == ProductType.QUANTITY && (
                        <div>
                          <span className="text-[#43474E] mt-2">
                            Total Estimated weight in Lbs:{' '}
                          </span>
                          <span className="text-gray-800 font-semibold">
                            <span className="text-gray-800 font-semibold">
                              {/* If product is processed, display total weight using final measurements. Otherwise, use initial quantity */}
                              {(
                                (product.productStatus >=
                                OrderProcessStatus.COMPLETED
                                  ? product.productFinalQuantity
                                  : product.productQuantity) * product?.weight
                              ).toFixed(2)}{' '}
                              {WeightType.LBS}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>

                    {product.addons?.length > 0 && (
                      <div>
                        <span className="font-semibold mb-2">Add-Ons:</span>
                        <div className="flex flex-col gap-1 py-2 text-[#43474E]">
                          {product.addons.map((addon) => (
                            <span key={addon.addonId}>{addon.addonName}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {product.packing?.length > 0 && (
                      <div>
                        <span className="font-semibold mb-2">Packaging:</span>
                        <div className="space-y-2 py-2">
                          {product.packing.map((pack) => {
                            const isVisible =
                              (pack.isFinalPacking === 1 &&
                                product.productStatus === 2) ||
                              (pack.isFinalPacking === 0 &&
                                product.productStatus !== 2);

                            if (!isVisible) return null;

                            return (
                              <div
                                key={pack.orderPackingId}
                                className="flex justify-between"
                              >
                                <span className="w-1/4 text-[#43474E]">
                                  {pack.packageType}
                                </span>

                                <span className="w-1/4 text-center">
                                  <span className="text-[#43474E]">Size: </span>
                                  {pack.packageSize}{' '}
                                  <span className="text-black">
                                    {WeightType.LBS}
                                  </span>
                                </span>

                                {product.packing.length > 1 && (
                                  <span className="w-1/4 text-center">
                                    <span className="text-[#43474E]">
                                      Pkg Split:{' '}
                                    </span>
                                    <span className="text-black">
                                      {pack.packingPercentage}%
                                    </span>
                                  </span>
                                )}

                                {product.productStatus === 2 && (
                                  <span className="w-1/4 text-right">
                                    <span className="text-[#43474E]">
                                      Price:{' '}
                                    </span>
                                    <span className="text-black">
                                      ${pack.packingPrice}
                                    </span>
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {product.orderPackingNotes && (
                      <div className="mt-3">
                        <h4 className="font-medium mb-2">Packaging Notes:</h4>
                        <span className="text-[#43474E]">
                          {product.orderPackingNotes}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="w-full mt-3">
                    {product.productStatus >= OrderProcessStatus.COMPLETED ? (
                      <span className="inline-block w-full bg-[#CCEECC] border border-[#2D5B4A] p-2 text-[#2D5B4A] text-center font-semibold rounded-md">
                        Product Processed & Packed{' '}
                        <FontAwesomeIcon
                          icon={faCircleCheck}
                          className="text-[#33795F]"
                        />
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          if (product.productStatus == 0) {
                            handleUpdateStatus(
                              product.orderProductId,
                              product.productName,
                            );
                          } else {
                            // Sending complete status - 2 if product is already started (1)
                            handleProductProcessing(
                              OrderProcessStatus.COMPLETED,
                              product.orderProductId,
                              product.productName,
                            );
                          }
                        }}
                        className="w-full mb-3 px-6 py-2 rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
                      >
                        Update Status
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          {/* Product Modal */}
          {showOrderProcessModal && (
            <OrderProcessBottomSheet
              onClose={() => setShowOrderProcessModal(false)}
              name={currentProductName}
              productId={currentProductId}
              onSelect={handleProductProcessing}
            />
          )}
        </div>
      </div>
    </div>
  );
};
